import { MapPin, Clock, CheckCircle, QrCode } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { useNavigate } from "react-router-dom";
import { useLanguage } from "@/hooks/useLanguage";
import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import pickupImage from "@/assets/pickup-point.jpg";

interface RelayPoint {
  id: string;
  name: string;
  address: string;
  hours_json: any | null;
  active: boolean;
}

const RelayPointsSection = () => {
  const navigate = useNavigate();
  const { t, language } = useLanguage();
  const [relayPoints, setRelayPoints] = useState<RelayPoint[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchRelayPoints = async () => {
      try {
        const { data, error } = await supabase
          .from('relays')
          .select('id, name, address, hours_json, active')
          .eq('active', true)
          .order('name')
          .limit(4);

        if (error) throw error;
        setRelayPoints(data || []);
      } catch (error) {
        console.error('Erreur chargement points relais:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchRelayPoints();
  }, []);

  const handleFindRelayPoint = () => {
    navigate('/relay-points');
  };

  // Formater les heures depuis hours_json
  const formatHours = (hours_json: any): string | null => {
    if (!hours_json) return null;
    // Si c'est un objet avec les jours, on affiche un résumé
    if (typeof hours_json === 'object') {
      const weekdays = hours_json.weekdays || hours_json.monday;
      const sunday = hours_json.sunday;
      if (weekdays && sunday) {
        return `Lun-Sam ${weekdays}, Dim ${sunday}`;
      }
    }
    return null;
  };

  // Déterminer si un point relais est ouvert (simplifié)
  const isOpen = (): boolean => {
    const now = new Date();
    const currentHour = now.getHours();
    const dayOfWeek = now.getDay();
    if (dayOfWeek === 0) return currentHour >= 9 && currentHour < 18; // Dimanche
    return currentHour >= 7 && currentHour < 21;
  };

  return (
    <section className="py-16">
      <div className="container">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Content */}
          <div className="space-y-8">
            <div className="space-y-4">
              <h2 className="text-3xl md:text-4xl font-bold">
                {t('relay.title')}
              </h2>
              <p className="text-lg text-muted-foreground">
                {t('relay.description')}
              </p>
            </div>

            {/* How it works */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="flex items-start space-x-3">
                <div className="flex-shrink-0 w-8 h-8 bg-primary/10 rounded-full flex items-center justify-center">
                  <span className="text-sm font-bold text-primary">1</span>
                </div>
                <div>
                  <h4 className="font-semibold">{t('relay.step1')}</h4>
                  <p className="text-sm text-muted-foreground">{t('relay.step1.desc')}</p>
                </div>
              </div>
              
              <div className="flex items-start space-x-3">
                <div className="flex-shrink-0 w-8 h-8 bg-primary/10 rounded-full flex items-center justify-center">
                  <span className="text-sm font-bold text-primary">2</span>
                </div>
                <div>
                  <h4 className="font-semibold">{t('relay.step2')}</h4>
                  <p className="text-sm text-muted-foreground">{t('relay.step2.desc')}</p>
                </div>
              </div>
              
              <div className="flex items-start space-x-3">
                <div className="flex-shrink-0 w-8 h-8 bg-primary/10 rounded-full flex items-center justify-center">
                  <span className="text-sm font-bold text-primary">3</span>
                </div>
                <div>
                  <h4 className="font-semibold">{t('relay.step3')}</h4>
                  <p className="text-sm text-muted-foreground">{t('relay.step3.desc')}</p>
                </div>
              </div>
              
              <div className="flex items-start space-x-3">
                <div className="flex-shrink-0 w-8 h-8 bg-success rounded-full flex items-center justify-center">
                  <CheckCircle className="h-4 w-4 text-success-foreground" />
                </div>
                <div>
                  <h4 className="font-semibold">{t('relay.step4')}</h4>
                  <p className="text-sm text-muted-foreground">{t('relay.step4.desc')}</p>
                </div>
              </div>
            </div>

            <Button size="lg" className="bg-gradient-primary" onClick={handleFindRelayPoint}>
              <MapPin className="mr-2 h-4 w-4" />
              {t('relay.find_point')}
            </Button>
          </div>

          {/* Relay Points List & Image */}
          <div className="space-y-6">
            {/* Featured Image */}
            <div className="aspect-video rounded-xl overflow-hidden shadow-elegant">
              <img
                src={pickupImage}
                alt="Point relais Nazandoo"
                className="w-full h-full object-cover"
              />
            </div>

            {/* Relay Points Cards */}
            <div className="space-y-3">
              {loading ? (
                <div className="text-center py-4 text-muted-foreground">
                  {language === 'fr' ? 'Chargement...' : 'Loading...'}
                </div>
              ) : relayPoints.length === 0 ? (
                <div className="text-center py-4 text-muted-foreground">
                  {language === 'fr' ? 'Aucun point relais disponible' : 'No relay points available'}
                </div>
              ) : (
                relayPoints.map((point) => {
                  const pointIsOpen = isOpen();
                  const hours = formatHours(point.hours_json);
                  return (
                    <Card key={point.id} className="hover:shadow-card transition-shadow">
                      <CardContent className="p-4">
                        <div className="flex items-center justify-between">
                          <div className="flex-1">
                            <div className="flex items-center space-x-2 mb-1">
                              <h4 className="font-semibold">{point.name}</h4>
                              <Badge 
                                variant={pointIsOpen ? "default" : "secondary"}
                                className={pointIsOpen ? "bg-success" : ""}
                              >
                                {pointIsOpen 
                                  ? (language === 'fr' ? 'Ouvert' : 'Open') 
                                  : (language === 'fr' ? 'Fermé' : 'Closed')}
                              </Badge>
                            </div>
                            <p className="text-sm text-muted-foreground mb-1">
                              {point.address}
                            </p>
                            {hours && (
                              <div className="flex items-center text-sm text-muted-foreground">
                                <Clock className="h-3 w-3 mr-1" />
                                {hours}
                              </div>
                            )}
                          </div>
                          <div className="text-right">
                            <QrCode className="h-4 w-4 text-muted-foreground ml-auto mt-1" />
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  );
                })
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default RelayPointsSection;